import React, { useState } from "react";
import {
  FieldValues,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { Button, Flex } from "@chakra-ui/react";
import { query, getDocs, where } from "firebase/firestore";

import { collection } from "firebase/firestore";
import { FirebaseContext } from "@contexts";
import { DB_COLLECTION_NAMES } from "@constants";

import JsonFormField from "./JsonFormField";
import { JsonFormControl, ConditionalLogic } from "./types";

interface IJsonForm<T extends FieldValues> {
  fields: JsonFormControl[];
  onSubmit: SubmitHandler<T>;
  onError?: SubmitErrorHandler<T>;
  isLoading?: boolean;
  disabled?: boolean;
  clearAfterSubmit?: boolean;
  displayClear?: boolean;
  submitButtonText?: string;
  clearButtonText?: string;
}

/**
 * A component that takes a list of form control elements and renders them as a HTML form object.
 * @param fields Form Controls used to create a Json Form.
 * @param submit Callback function to submit form contents
 */
function JsonForm<T extends FieldValues>({
  fields,
  onSubmit,
  isLoading,
  disabled = false,
  clearAfterSubmit = false,
  onError = console.error,
  displayClear = false,
  submitButtonText = "Submit",
  clearButtonText = "Clear",
}: IJsonForm<T>) {
  const defaultFormValues = React.useMemo(
    () => computeDefaultFormState(fields),
    [fields],
  );
  const formMethods = useForm({ defaultValues: defaultFormValues });
  const [formData, setFormData] = useState({});

  const { firestore } = React.useContext(FirebaseContext);

  const hasRequiredElements = fields
    .map((field) => field.config || {})
    .reduce((requiredFlag, option) => requiredFlag || !!option.required, false);

  /**
   * Wrapper for the onSubmut prop that optionally clears the form after the submission is made.
   */
  const doSubmit = React.useCallback<SubmitHandler<FieldValues>>(
    function (data: T, event?: React.BaseSyntheticEvent) {
      onSubmit(data, event);
      if (clearAfterSubmit) formMethods.reset();
    } as SubmitHandler<FieldValues>,
    [],
  );

  function testConditionalLogic(
    field: JsonFormControl,
    fieldName: string,
    fieldValue: string,
  ) {
    if (!field.conditionalLogic) return true;
    const passes = passesConditionalLogic(
      field.conditionalLogic,
      fieldName,
      fieldValue,
    );
    return passes;
  }

  function testCorrectField(field: JsonFormControl, fieldName: string) {
    if (!field.conditionalLogic) return true;
    const passes = isCorrectField(field.conditionalLogic, fieldName);
    return passes;
  }

  const handleChange = async (e: React.FormEvent<HTMLFormElement>) => {
    const { name, value } = e.target as HTMLSelectElement;
    for (const field of fields) {
      if (field.conditionalLogic && field.config) {
        const passes = testConditionalLogic(field, name, value);
        const correctField = testCorrectField(field, name);
        if (correctField) {
          if (passes) {
            field.config.required = false;
            setFormData({ ...formData });
          } else if (!passes) {
            field.config.required = true;
            setFormData({ ...formData });
          }
        }
      }

      if (field.dependency) {
        const correctField = testCorrectField(field, name);
        if (correctField) {
          const collectionName = DB_COLLECTION_NAMES.STEPS;
          const collectionRef = collection(firestore, collectionName);

          const queryResultSerials2S = query(
            collectionRef,
            where("stepName", "==", "2S_GLUE_SANDWICH"),
            where("moduleId", "==", value),
          );

          const queryResultSerialsPS = query(
            collectionRef,
            where("stepName", "==", "PS_SENSOR_BASEPLATE_GLUING"),
            where("moduleId", "==", value),
          );

          const querySnapshotSerials2S = await getDocs(queryResultSerials2S);
          const querySnapshotSerialsPS = await getDocs(queryResultSerialsPS);
          let topSensorSerial = "";
          let bottomSensorSerial = "";
          let PSSensorSerial = "";
          querySnapshotSerials2S.forEach((doc) => {
            topSensorSerial = doc ? doc.data().details.topSensorSerial : "";
            bottomSensorSerial = doc
              ? doc.data().details.bottomSensorSerial
              : "";
          });
          querySnapshotSerialsPS.forEach((doc) => {
            PSSensorSerial = doc ? doc.data().details.sensorSerial : "";
          });

          const queryResultTop = query(
            collectionRef,
            where("stepName", "==", "AUX_SCRATCHPAD"),
            where("moduleId", "==", topSensorSerial),
          );

          const queryResultBottom = query(
            collectionRef,
            where("stepName", "==", "AUX_SCRATCHPAD"),
            where("moduleId", "==", bottomSensorSerial),
          );

          const queryResultPS = query(
            collectionRef,
            where("stepName", "==", "AUX_SCRATCHPAD"),
            where("moduleId", "==", PSSensorSerial),
          );

          const querySnapshotTop = await getDocs(queryResultTop);
          const querySnapshotBottom = await getDocs(queryResultBottom);
          const querySnapshotPS = await getDocs(queryResultPS);

          let scratchpadTop = "";
          let scratchpadBottom = "";
          let scratchpadPS = "";
          querySnapshotTop.forEach((doc) => {
            scratchpadTop = doc ? doc.data().details.scratchpad : "";
          });

          querySnapshotBottom.forEach((doc) => {
            scratchpadBottom = doc ? doc.data().details.scratchpad : "";
          });

          querySnapshotPS.forEach((doc) => {
            scratchpadPS = doc ? doc.data().details.scratchpad : "";
          });

          if (scratchpadTop != "" && scratchpadTop != undefined) {
            formMethods.setValue("scratchpadTop", scratchpadTop);
          }
          if (scratchpadBottom != "" && scratchpadBottom != undefined) {
            formMethods.setValue("scratchpadBottom", scratchpadBottom);
          }
          if (scratchpadPS != "" && scratchpadPS != undefined) {
            formMethods.setValue("scratchpadPS", scratchpadPS);
          }
        }
      }
    }
  };

  return (
    <form
      onSubmit={formMethods.handleSubmit(
        doSubmit as SubmitHandler<FieldValues>,
        onError as SubmitErrorHandler<FieldValues>,
      )}
      onChange={handleChange}
    >
      <Flex direction="column" gap="8px">
        <FormProvider {...formMethods}>
          {fields.map((field: JsonFormControl) => (
            <JsonFormField
              key={field.fieldName}
              field={field}
              disabled={disabled}
            />
          ))}
        </FormProvider>
        {hasRequiredElements && (
          <p>
            <span>*</span> Required
          </p>
        )}
        <Flex
          style={{ width: "100%" }}
          direction="row"
          alignItems="baseline"
          justifyContent="space-around"
        >
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            disabled={disabled}
          >
            {submitButtonText}
          </Button>
          {displayClear && (
            <Button type="reset" variant="outline">
              {clearButtonText}
            </Button>
          )}
        </Flex>
      </Flex>
    </form>
  );
}

/**
 * Generates a JS Object of default values for fields used in the Json Form.
 * @param formControls Form Controls passed to create the form.
 * @returns Object of key -> value pairs defining an default values for the form.
 */
function computeDefaultFormState(formControls: JsonFormControl[]) {
  return Object.fromEntries(
    formControls.map((formControl) => {
      const defaultValue =
        formControl.inputType === "date"
          ? new Date(formControl.defaultValue)
          : formControl.defaultValue;
      return [formControl.fieldName, defaultValue];
    }),
  );
}

export const isCorrectField = (
  conditionalLogic: ConditionalLogic[] | undefined,
  fieldName: string,
): boolean => {
  if (!conditionalLogic) {
    return true;
  }

  for (let i = 0; i < conditionalLogic.length; i += 1) {
    const condLogic = conditionalLogic[i];
    if (condLogic.depFieldName == fieldName) return true;
  }
  return false;
};

export const passesConditionalLogic = (
  conditionalLogic: ConditionalLogic[] | undefined,
  fieldName: string,
  fieldValue: string,
): boolean => {
  if (!conditionalLogic) {
    return true;
  }

  let res = true;
  let runningCond = true;
  for (let i = 0; i < conditionalLogic.length; i += 1) {
    const condLogic = conditionalLogic[i];
    const condOp = condLogic.depFieldValueCondition;
    const expectedValue = condLogic.depFieldValue;

    if (condLogic.depFieldName == fieldName) {
      res = evalConditionalLogic(fieldValue, condOp, expectedValue);
    }

    runningCond = res;
    if (runningCond) {
      break;
    }
  }
  return runningCond;
};

const evalConditionalLogic = (
  value: string,
  condOp: string,
  expectedValue: string | number,
): boolean => {
  let res;
  switch (condOp) {
    case "NotEmpty":
      res = value?.length > 0;
      break;
    case "!=":
      res = value !== expectedValue;
      break;
    case "<":
      res = value < expectedValue;
      break;
    case "<=":
      res = value <= expectedValue;
      break;
    case "=":
      res = value === expectedValue;
      break;
    case ">":
      res = value > expectedValue;
      break;
    default:
      res = false;
  }
  return res;
};

export { JsonForm, type IJsonForm };
